// [CF 2026-08-17 #7 / V4] Every business can hold several phone numbers — the
// owner form has an "Additional Phone Numbers" field — but the whole site only
// ever read the single `mobileNumber` column, so the extras were saved and never
// shown. The client reported this as "only one company phone number is
// displayed".
//
// Two things make the stored value awkward:
//
//   1. The API returns `mobileNumbers` as a JSON **string**, not an array:
//        "[{\"countryCode\":\"+965\",\"mobileNumber\":\"55970055\"}, ...]"
//      so it has to be parsed before it can be read.
//
//   2. It usually repeats the primary number. The owner form appends the extra
//      numbers onto a list that already contains the primary, so a business with
//      one extra number comes back with the primary listed twice. Verified on
//      live data: one dealer holds 55970055, 55970055, 50510122.
//
// `parsePhoneNumbers` hides both: give it a business record and it returns a
// clean, de-duplicated list with the primary first.

export const digitsOnly = (v) => String(v ?? '').replace(/\D/g, '');

/**
 * @returns {{countryCode:string, mobileNumber:string}[]} primary first, deduped.
 */
export function parsePhoneNumbers(record) {
  if (!record) return [];

  const collected = [];
  const add = (countryCode, mobileNumber) => {
    const number = String(mobileNumber ?? '').trim();
    if (!number) return;
    collected.push({
      countryCode: String(countryCode ?? '').trim(),
      mobileNumber: number,
    });
  };

  // Primary first — it is the number the business is listed under.
  add(record.mobileNumberCountryCode, record.mobileNumber);

  let extra = record.mobileNumbers;
  if (typeof extra === 'string') {
    try {
      extra = JSON.parse(extra);
    } catch (e) {
      extra = [];
    }
  }
  if (Array.isArray(extra)) {
    extra.forEach((entry) => add(entry?.countryCode, entry?.mobileNumber));
  }

  /* De-duplicate on the NATIONAL digits, ignoring the country code. The same
     number is stored inconsistently — sometimes "+965" + "55970055", sometimes
     "965 55970055" with the code folded in — so comparing the full string would
     leave visible duplicates in the picker. */
  const seen = new Set();
  return collected.filter((p) => {
    const national = digitsOnly(p.mobileNumber);
    if (!national || seen.has(national)) return false;
    seen.add(national);
    return true;
  });
}

/** `tel:` target for one entry. tel: accepts a leading "+", so it is kept. */
export function telHref(phone) {
  if (!phone) return null;
  const code = String(phone.countryCode ?? '').trim();
  const number = String(phone.mobileNumber ?? '').trim();
  if (!number) return null;
  const prefix = code && !number.startsWith(code) ? code : '';
  return `tel:${prefix}${number}`.replace(/\s+/g, '');
}

/** How the number reads in the picker. */
export function formatPhone(phone) {
  const code = String(phone?.countryCode ?? '').trim();
  const number = String(phone?.mobileNumber ?? '').trim();
  if (!code || number.startsWith(code)) return number;
  return `${code} ${number}`;
}
